/*
 * Copyright (c) 2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { GlobalTransaction } from '@cp/metering-sdk'
import { BillingHistoryHelpers } from '../BillingHistoryHelpers'
import { DEFAULT_RENDER_FUNCTION, getColumnValue } from './TableColumnHelpers'
import { ColumnDefinitionFn, ColumnRendererRecord, ValidBillingHistoryColumn } from './types'

const COLUMN_TITLE_MAPPING = {
  createdAt: 'Transaction Date',
  transactionId: 'Transaction ID',
  studioNamespace: 'Organization Name',
  products: 'Type',
  amount: 'Amount',
  status: 'Payment Status'
} satisfies Record<ValidBillingHistoryColumn, string>

const DEFAULT_COLUMN_RENDERER_MAPPING = {
  createdAt: createdAt => createdAt ?? '-',
  transactionId: undefined,
  studioNamespace: undefined,
  products: (_, { products }) => {
    // `products` array might have an empty string => [""]
    const cleanedArray = products.filter(Boolean)
    if (cleanedArray.length) {
      return cleanedArray.join(', ')
    }
    return '-'
  },
  amount: value => BillingHistoryHelpers.getBillingAmount(value),
  status: value => BillingHistoryHelpers.getPaymentStatusInfo(value).text
} satisfies ColumnRendererRecord<GlobalTransaction, ValidBillingHistoryColumn>

const DEFAULT_COLUMNS_RECORD = [
  'createdAt',
  'transactionId',
  'studioNamespace',
  'products',
  'amount',
  'status'
] satisfies Array<ValidBillingHistoryColumn>

export class BillingHistoryTableColumnDefinitionHelpers {
  static VALID_BILLING_HISTORY_COLUMNS = Object.keys(DEFAULT_COLUMN_RENDERER_MAPPING) as Array<ValidBillingHistoryColumn>
  static DEFAULT_COLUMNS_RECORD = DEFAULT_COLUMNS_RECORD

  static getDefaultBillingHistoryColumnsTitleMapping = (
    columnsKey: Array<ValidBillingHistoryColumn> | undefined = DEFAULT_COLUMNS_RECORD
  ) => {
    const columnTitle: string[] = []
    for (const key of columnsKey) {
      const mappedColumnTitleFromKey = COLUMN_TITLE_MAPPING[key]
      if (mappedColumnTitleFromKey) {
        columnTitle.push(mappedColumnTitleFromKey)
      }
    }

    return columnTitle
  }

  static getBillingHistoryColumn: ColumnDefinitionFn<GlobalTransaction, ValidBillingHistoryColumn> = (param, renderFn) => ({
    name: param,
    title: COLUMN_TITLE_MAPPING[param],
    render: record => {
      const effectiveRenderFn = renderFn ?? (DEFAULT_COLUMN_RENDERER_MAPPING[param] as typeof renderFn) ?? DEFAULT_RENDER_FUNCTION
      const effectiveValue = getColumnValue(record, param)
      return effectiveRenderFn(effectiveValue, record)
    }
  })
}
